import { features, mediaItems, about, comments, indexItems } from '@/mocks/homeData';

// ---- Features Section ----

export function FeaturesSection() {
  return (
    <section id="features" className="py-10 md:py-14 bg-background-100">
      <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6">
        <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-8">特色优势</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
          {features.map((feat) => (
            <div key={feat.id} className="bg-background-50 rounded-lg p-6 text-center border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer group">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center group-hover:bg-accent-200 transition-colors">
                <i className={`${feat.icon} text-accent-600 text-xl w-6 h-6 flex items-center justify-center`}></i>
              </div>
              <h3 className="text-base font-medium text-foreground-900 mb-2">{feat.title}</h3>
              <p className="text-xs text-foreground-500 leading-relaxed">{feat.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- Media Section ----

export function MediaSection() {
  return (
    <section id="media" className="py-10 md:py-14 bg-background-50">
      <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6">
        <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-8">精彩预告</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {mediaItems.map((item) => (
            <article key={item.id} className="bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer group">
              <div className="relative w-full aspect-[16/10] overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  title={item.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                />
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-background-50/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="ri-play-fill text-accent-600 text-lg w-5 h-5 flex items-center justify-center"></i>
                  </div>
                </div>
                <div className="absolute bottom-2 right-2 text-[10px] px-2 py-0.5 bg-black/60 text-background-50 rounded-md font-medium">
                  {item.duration}
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-sm font-medium text-foreground-900 mb-1">{item.title}</h3>
                <p className="text-xs text-foreground-500">{item.desc}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- About Section ----

export function AboutSection() {
  return (
    <section id="about" className="py-10 md:py-14 bg-background-100">
      <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6">
        <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-8">关于6080电影网</h2>
        <div className="max-w-3xl mx-auto bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/70">
          {about.paragraphs.map((p, idx) => (
            <p key={idx} className={`text-sm text-foreground-600 leading-relaxed ${idx < about.paragraphs.length - 1 ? 'mb-4' : ''}`}>
              {p}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- Comments Section ----

export function CommentsSection() {
  return (
    <section id="comments" className="py-10 md:py-14 bg-background-50">
      <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6">
        <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-8">影迷心声</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {comments.map((comment) => (
            <article key={comment.id} className="bg-background-100 rounded-lg p-5 border border-background-200/70">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                  <img
                    src={comment.avatar}
                    alt={comment.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                  />
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-900">{comment.name}</div>
                  <div className="text-[10px] text-foreground-400">{comment.date}</div>
                </div>
                <div className="ml-auto flex items-center gap-0.5">
                  {Array.from({ length: comment.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill w-3 h-3 flex items-center justify-center text-accent-500"></i>
                  ))}
                </div>
              </div>
              <p className="text-xs text-foreground-600 leading-relaxed">{comment.content}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- Index Section ----

export function IndexSection() {
  return (
    <section id="index" className="py-10 md:py-14 bg-background-100">
      <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6">
        <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-8">主题合集</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
          {indexItems.map((item) => (
            <div key={item.id} className="bg-background-50 rounded-lg p-5 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-colors cursor-pointer text-center group">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center group-hover:bg-accent-200 transition-colors">
                <i className={`${item.icon} text-accent-600 text-lg w-5 h-5 flex items-center justify-center`}></i>
              </div>
              <h3 className="text-sm font-medium text-foreground-900 mb-1 whitespace-nowrap">{item.title}</h3>
              <p className="text-[10px] text-foreground-400 mb-2">{item.count}部影片</p>
              <p className="text-[10px] text-foreground-500 leading-relaxed line-clamp-2">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}